<template>
    <section :class="[$style['mds-section'], classObject]" :data-mds-version="version">
        <div
            :tabindex="tabIndex"
            :class="[$style['mds-section__header-container'], headerClasses, sizeClass]"
            @click="toggle($event)"
            @keydown.enter="toggle($event)"
            :aria-expanded="ariaExpanded"
            :aria-controls="collapsible ? expandedId : null"
        >
            <mds-icon
                v-if="collapsible"
                name="triangle-fill-right"
                :class="$style['mds-section__toggle']"
                :size="iconSize"
            />
            <component :is="headingType" :aria-label="ariaLabel" :class="$style['mds-section__title']">
                <slot name="mds-section-title">{{ title }}</slot>
                <div v-if="titleDescription" :class="$style['mds-section__title-description']">
                    <mds-button
                        :size="descriptionIconSize"
                        :icon="titleDescriptionIcon"
                        variation="icon-only"
                        @mouseover="$emit('mds-section-icon-mouseover', $event)"
                        @mouseleave="$emit('mds-section-icon-mouseleave', $event)"
                        @click.stop="$emit('mds-section-icon-click', $event)"
                        :onDark="onDark"
                    ></mds-button>
                </div>
                <div :class="$style['mds-section__title-description']" v-if="hasTitleDescription">
                    <slot name="mds-section-title-description"></slot>
                </div>
            </component>
            <div
                :class="$style['mds-section__supplemental-content']"
                v-if="this.$slots['mds-section-supplemental-content']"
            >
                <slot name="mds-section-supplemental-content"></slot>
            </div>
            <div @click.stop :class="$style['mds-section__actions']" v-if="this.$slots['mds-section-actions']">
                <slot name="mds-section-actions"></slot>
            </div>
        </div>
        <div
            v-show="!collapsible || (open && collapsible)"
            :class="$style['mds-section__content']"
            :id="collapsible ? expandedId : null"
        >
            <template v-if="content">
                <span v-html="content"></span>
            </template>
            <template v-else>
                <slot />
            </template>
        </div>
    </section>
</template>

<script>
    import { MdsButton } from '@mds/button';
    import MdsIcon from '@mds/icon';
    import SectionProps from './section_props.js';

    export default {
        name: 'MdsSection',
        components: {
            MdsButton,
            MdsIcon,
        },
        data() {
            return {
                open: this.expanded,
            };
        },
        props: SectionProps,
        computed: {
            ariaExpanded() {
                if (!this.collapsible) {
                    return null;
                }
                return this.open ? 'true' : 'false';
            },
            descriptionIconSize() {
                return this.size > 5 ? 'small' : 'medium';
            },
            headingType() {
                return `h${this.headingLevel}`;
            },
            classObject() {
                const obj = {};

                obj[this.$style['mds-component--on-dark']] = this.onDark;
                obj[this.$style['mds-section--expanded']] = this.open;
                obj[this.$style['mds-section--collapsible']] = this.collapsible;
                obj[this.$style['mds-section--container']] = this.container;
                return obj;
            },
            headerClasses() {
                const obj = {};

                obj[this.$style['mds-section--border-bottom']] = this.borderPosition === 'bottom' || this.container;
                obj[this.$style[`mds-section--${this.border}`]] = this.border;
                obj[this.$style['mds-section--font-bold']] = this.bold;

                return obj;
            },
            sizeClass() {
                return this.$style[`mds-section--level-${this.size}`];
            },
            iconSize() {
                return this.size < 6 ? 'medium' : 'small';
            },
            tabIndex() {
                return this.collapsible ? 0 : null;
            },
            expandedId() {
                const randomId = Math.random() * Math.floor(1000000);
                return `mds-section-${Math.round(randomId)}`;
            },
            hasTitleDescription() {
                return !!this.$slots['mds-section-title-description'];
            },
        },
        methods: {
            toggle(event) {
                this.open = !this.open;
                const eventName = this.open ? 'expanded' : 'closed';
                this.$emit(`mds-section-${eventName}`, event.target);
            },
        },
    };
</script>

<style lang="scss" module>
    @import './section.scss';
</style>
